// Default Sections
import { VolumeBonusDefinition, VolumeBonusTier, VolumeBonusesTier } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";
import { getExactMatrixDimensions } from "@/Common/helpers/Matrices";
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";
import { isLastValid, isValidMatrixRange } from "@/Common/helpers/Matrices/tiers";

const CONSTANTS = {
	PRE_OWNED_LABEL: "Pre-Owned Vehicles",
	COMBINED_LABEL: "Combined",
	MINIMUM_GROSS_INPUT: 1,
	MAXIMUM_GROSS_INPUT: 100000000,
	MINIMUM_UNITS_INPUT: 1,
	MAXIMUM_UNITS_INPUT: 10000,
	INPUT_VOLUME_BONUSES: 100000000,
};

const mapDefinitionsToArray = (definition = null) => {
	const result = [];
	if (definition) {
		for (const [key, value] of Object.entries(definition)) {
			const valueType = (typeof value).toUpperCase();
			if (key && !Array.isArray(value) && valueType === "OBJECT") {
				result.push(value);
			}
		}
	}
	return result;
};

const filterErrorsList = (errors, keys) => errors.filter((error) => !keys.includes(error));

const getVolumeBonusTabs = (bonuses = []) => bonuses?.map((element) => element?.make || CONSTANTS.PRE_OWNED_LABEL);

const getCombinedBonuses = ({ content, position, bonuses = [], franchises = [], combineFranchises = false }) => {
	const combined = [];
	let additionalBonuses = [];
	const inputDefaultType = position === "Sales Manager" ? "Percent" : "Dollar";

	if (combineFranchises) {
		const isNewIncluded = bonuses?.some((element) => element.make === "New Vehicles");
		if (!isNewIncluded) {
			combined.push(
				VolumeBonusDefinition("New Vehicles", [
					VolumeBonusesTier(-CONSTANTS.INPUT_VOLUME_BONUSES, CONSTANTS.INPUT_VOLUME_BONUSES, [
						VolumeBonusTier({ from: 0, to: 1000, bonus: 0, bonusInput: inputDefaultType }),
					]),
				])
			);
		}
		additionalBonuses = bonuses?.filter((element) => !franchises.includes(element.make));
	} else {
		const contentOriginalList = mapDefinitionsToArray(content);
		if (franchises?.length > 1) {
			franchises.forEach((franchise) => {
				combined.push(
					VolumeBonusDefinition(franchise, [
						VolumeBonusesTier(-CONSTANTS.INPUT_VOLUME_BONUSES, CONSTANTS.INPUT_VOLUME_BONUSES, [
							VolumeBonusTier({ from: 0, to: 1000, bonus: 0, bonusInput: inputDefaultType }),
						]),
					])
				);
			});
		} else {
			combined.push(bonuses[0]);
		}
		contentOriginalList?.forEach((element) => {
			const bonusToKeep = bonuses.find((bonus) => bonus.make === element.make);
			if (bonusToKeep) {
				combined.push(bonusToKeep);
			}
		});
	}
	return [...combined, ...additionalBonuses];
};

const getPotentialTabsErrorsKeys = (section, tabs = []) => tabs?.map((tab) => getErrorKey(section, tab));

const getErrorsToRemove = (errors = [], errorsToKeep = []) => {
	if (errors.length) {
		return errors.filter((key) => !errorsToKeep.includes(key));
	}
	return [];
};

const removeTabBonus = (bonuses = [], tabNames = []) => bonuses.filter((bonus) => !tabNames.includes(bonus.make));

const getTabBonus = (bonuses = [], tabName = "") => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { _id, ...bonus } = bonuses.find((item) => item.make === tabName);
	return bonus || null;
};

const mapModelToPropagate = (bonuses = [], tabs = null) => {
	const preOwned = getTabBonus(bonuses, CONSTANTS.PRE_OWNED_LABEL);
	const combined = getTabBonus(bonuses, "Combined");
	const bonusToSend = removeTabBonus(bonuses, [CONSTANTS.PRE_OWNED_LABEL, "Combined"]);

	return {
		bonuses: bonusToSend,
		preOwned: preOwned || null,
		combined: combined || null,
		combineFranchises: Boolean(tabs?.combineFranchises),
		globalUnitCounts: Boolean(tabs?.globalUnitCounts),
		newAndPreOwnedRequired: Boolean(tabs?.newAndPreOwnedRequired),
	};
};

const setPeriphery = (bonuses, data) => {
	bonuses?.forEach((bonus) => {
		bonus?.tiers?.forEach((tier, tierIndex) => {
			tier.from.value = data?.[tierIndex]?.from?.value;
			tier.to.value = data?.[tierIndex]?.to?.value;
			tier?.bonuses?.forEach((innerBonus, innerBonusIndex) => {
				innerBonus.from.value = data?.[tierIndex]?.bonuses?.[innerBonusIndex]?.from?.value;
				innerBonus.to.value = data?.[tierIndex]?.bonuses?.[innerBonusIndex]?.to?.value;
			});
		});
	});

	return bonuses;
};

const synchronizePeriphery = (updatedModel, tabIndex) => {
	const bonuses = [...updatedModel.bonuses];
	if (updatedModel?.tabsProps?.globalUnitCounts && updatedModel?.tabsProps?.newAndPreOwnedRequired) {
		// if the switch was switched to on, we synchronize
		const bonusToCopy = bonuses[tabIndex];
		// remove bonus to copy from bonuses list
		const filteredBonuses = bonuses.filter((element, index) => {
			if (index !== tabIndex) {
				return element;
			}
			return null;
		});
		// for each element in the bonuses list, set it to the data we have from bonusToCopy
		setPeriphery(filteredBonuses, bonusToCopy?.tiers);
	}
};

const expandBonus = ({ bonus, preventSave, sectionId, maxTiers = 1, maxColumns = 1 }) => {
	if (bonus?.tiers?.length < maxTiers) {
		preventSave.current.push(getErrorKey(sectionId, bonus?.make));
		bonus.tiers[bonus.tiers.length - 1].to.value = 0;
		while (bonus?.tiers?.length < maxTiers) {
			const newTier = clone(bonus.tiers[bonus.tiers.length - 1]);
			newTier.from.value = CONSTANTS.MINIMUM_GROSS_INPUT + 0.01;
			newTier.to.value = 0;
			newTier.bonuses.forEach((item) => {
				item.bonus.value = 0;
			});
			bonus?.tiers?.push(newTier);
		}
		bonus.tiers[bonus.tiers.length - 1].to.value = CONSTANTS.MAXIMUM_GROSS_INPUT;
	}

	for (const tiers of bonus.tiers) {
		const tierBonuses = tiers?.bonuses;
		if (tierBonuses?.length < maxColumns) {
			preventSave.current.push(getErrorKey(sectionId, bonus?.make));
			tierBonuses[tierBonuses.length - 1].to.value = 0;
			const newTierBonus = clone(tierBonuses[tierBonuses.length - 1]);
			newTierBonus.from.value = CONSTANTS.MINIMUM_UNITS_INPUT;
			newTierBonus.to.value = 0;
			newTierBonus.bonus.value = 0;
			while (tierBonuses?.length < maxColumns) {
				tierBonuses.push(clone(newTierBonus));
			}
		}
		tierBonuses[tierBonuses.length - 1].to.value = CONSTANTS.MAXIMUM_UNITS_INPUT;
	}

	return clone(bonus);
};

const isOnlyExpandible = ({ tiersDifference, columnsDifference, expand }) => tiersDifference >= 0 && columnsDifference >= 0 && expand;
const isOnlyShrinkable = ({ tiersDifference, columnsDifference, shrink }) => tiersDifference <= 0 && columnsDifference <= 0 && shrink;

const getResizeOperations = (args) => {
	const { tiersDifference, columnsDifference, newMatrixDimensions, matrixDimensionsForBonus, expand, shrink } = args;

	if (tiersDifference >= 0 && columnsDifference <= 0) {
		// Should expand first
		return [
			{ executable: expand, operation: "expand" },
			{ executable: shrink, operation: "shrink" },
		]
			.filter((item) => item.executable)
			.map((item) => {
				const params =
					item.operation === "expand"
						? {
								tiers: newMatrixDimensions.tiers,
								columns: matrixDimensionsForBonus.columns,
						  }
						: {
								tiers: newMatrixDimensions.tiers,
								columns: newMatrixDimensions.columns,
						  };

				return {
					...item,
					...params,
				};
			});
	} else if (tiersDifference <= 0 && columnsDifference >= 0) {
		// Should shrink first
		return [
			{ executable: shrink, operation: "shrink" },
			{ executable: expand, operation: "expand" },
		]
			.filter((item) => item.executable)
			.map((item) => {
				const params =
					item.operation === "expand"
						? {
								tiers: newMatrixDimensions.tiers,
								columns: newMatrixDimensions.columns,
						  }
						: {
								tiers: newMatrixDimensions.tiers,
								columns: matrixDimensionsForBonus.columns,
						  };
				return {
					...item,
					...params,
				};
			});
	}

	return [];
};

const getResizedBonus = (bonus, args) => {
	const {
		preventSave,
		sectionId,
		tiersDifference,
		columnsDifference,
		newMatrixDimensions,
		matrixDimensionsForBonus,
		expand = true,
		shrink = true,
	} = args;

	if (isOnlyExpandible({ tiersDifference, columnsDifference, expand })) {
		// tiers got bigger
		// columns got bigger
		return expandBonus({
			bonus,
			preventSave,
			sectionId,
			maxTiers: newMatrixDimensions.tiers,
			maxColumns: newMatrixDimensions.columns,
		});
	}

	if (isOnlyShrinkable({ tiersDifference, columnsDifference, shrink })) {
		// tiers got smaller
		// columns got smaller
		return shrinkBonus(bonus, newMatrixDimensions.tiers, newMatrixDimensions.columns);
	}

	const operations = getResizeOperations({
		tiersDifference,
		columnsDifference,
		newMatrixDimensions,
		matrixDimensionsForBonus,
		expand,
		shrink,
	});

	operations.forEach((definition) => {
		const { operation, tiers, columns } = definition;
		if (operation === "expand") {
			bonus = expandBonus({
				bonus,
				preventSave,
				sectionId,
				maxTiers: tiers,
				maxColumns: columns,
			});
		} else {
			bonus = shrinkBonus(bonus, tiers, columns);
		}
	});

	return bonus;
};

const resizeBonuses = ({ bonuses, preventSave, sectionId, newMatrixDimensions, expand = true, shrink = true }) => {
	if (!bonuses?.length) {
		return bonuses;
	}

	const isCombinedIncluded = bonuses.some((bonus) => bonus?.make === CONSTANTS.COMBINED_LABEL);
	const bonusesToResize = bonuses.filter((bonus) => bonus?.make !== CONSTANTS.COMBINED_LABEL);
	const combinedTemporal = bonuses.find((bonus) => bonus?.make === CONSTANTS.COMBINED_LABEL);

	if (expand || shrink) {
		bonusesToResize.forEach((bonus, key) => {
			const matrixDimensionsForBonus = getExactMatrixDimensions("bonuses", bonus?.tiers);
			if (!isSimilarObject(newMatrixDimensions, matrixDimensionsForBonus)) {
				const tiersDifference = newMatrixDimensions.tiers - matrixDimensionsForBonus.tiers;
				const columnsDifference = newMatrixDimensions.columns - matrixDimensionsForBonus.columns;

				bonusesToResize[key] = getResizedBonus(bonus, {
					preventSave,
					sectionId,
					tiersDifference,
					columnsDifference,
					newMatrixDimensions,
					matrixDimensionsForBonus,
					expand,
					shrink,
				});
			}
		});
	}

	const resultBonuses = clone(bonusesToResize);
	const result = resultBonuses.map((tab) => {
		if (tab?.tiers?.length === 1 && tab?.tiers[0]?.bonuses?.length === 1) {
			tab.tiers[0].bonuses[0].bonus.value = 0;
		}
		return tab;
	});

	if (isCombinedIncluded) {
		result.push(combinedTemporal);
	}

	return result;
};

const shrinkBonus = (bonus, maxTiers = 1, maxColumns = 1) => {
	// Reducing rows tiers
	bonus.tiers.length = maxTiers;
	bonus.tiers[maxTiers - 1].to.value = CONSTANTS.MAXIMUM_GROSS_INPUT;

	for (const tiers of bonus.tiers) {
		const tierBonuses = tiers.bonuses;

		// Reducing columns tiers
		tierBonuses.length = maxColumns;
		tierBonuses[maxColumns - 1].to.value = CONSTANTS.MAXIMUM_UNITS_INPUT;
	}

	return clone(bonus);
};

const isSomeConfiguredModel = (content) => {
	const { makes = [] } = content || {};
	if (!makes?.length) {
		return true;
	}
	return makes?.some((item) => {
		const isMatrixInRange = isValidMatrixRange(item?.tiers || [], { column: "bonuses" });
		const isValidLast = isLastValid(item?.tiers || [], { column: "bonuses", reference: "bonus" });
		return isMatrixInRange && isValidLast;
	});
};

export {
	mapDefinitionsToArray,
	filterErrorsList,
	getVolumeBonusTabs,
	getCombinedBonuses,
	getPotentialTabsErrorsKeys,
	getErrorsToRemove,
	removeTabBonus,
	getTabBonus,
	mapModelToPropagate,
	setPeriphery,
	synchronizePeriphery,
	resizeBonuses,
	shrinkBonus,
	expandBonus,
	isSomeConfiguredModel,
};
